/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getId } from '@fluentui/react/lib/Utilities';
import flow from 'lodash.flow';
import * as React from 'react';
import { useAuthKeys } from 'src/core/hooks/useAuthKeys';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { addToast } from 'src/core/utils/decorators/addToast';
import { addTracker } from 'src/core/utils/decorators/addTracker';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { commonDemosMessages, resourceEndpointCurlPlaceholder } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { textAnalyticsDemosSupportedRegion } from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportedRegion';
import { ConversationSummarizationDemoSamplesJsonOutput } from 'src/languageStudio/appShell/prebuiltDemos/utils/signedOutMockedSamplesJsonOutput';
import { Language } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { getConversationAnalysisService } from 'src/languageStudio/core/services/conversation/conversationAnalysisService';
import {
    Conversation,
    ConversationInput,
    ConversationItem,
    ConversationParticipantRole,
    SummaryAspects
} from 'src/languageStudio/core/services/conversation/conversationSwaggers';
import { getTextAnalyticsService } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsService';
import {
    DetectedLanguagesDocuments,
    TextAnalyticsResultErrorsList
} from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';

export const useConversationCapabilities = () => {
    const tracker = useProgressTracker();
    const { rootStore } = useGlobalState();
    const { localize } = useLocalization();
    const textAnalyticsService = getTextAnalyticsService();
    const conversationService = getConversationAnalysisService();

    const { isSignedIn } = rootStore.authStore;
    const azureResource = rootStore.azureStore.session.csAccountStore;
    const currentServiceKey = rootStore.azureStore.session.serviceKey;

    const { authorizationHeader } = useAuthKeys({ currentServiceKey, currentResource: azureResource });

    const [detectedLanguages, setDetectedLanguages] = React.useState<DetectedLanguagesDocuments>();
    const [conversationSummarization, setConversationSummarization] = React.useState<Conversation>();

    const resetResults = () => {
        tracker.reset();
        setDetectedLanguages(null);
        setConversationSummarization(null);
    };

    const preValidate = (
        language: boolean,
        input: boolean,
        resourceExtraInvalidator: (resource: AzureCsAccountStore) => boolean = () => false,
        invalidResourceErrorMessage: string = localize(commonDemosMessages.azureResourceInvalidRegionAndSkuErrorMessage)
    ) => {
        let error: Error;
        const isInvalidInput = !input;
        const isInvalidLanguage = !language;
        const isInvalidResource =
            textAnalyticsDemosSupportedRegion.every(r => r !== azureResource?.region) || resourceExtraInvalidator(azureResource);

        if (isInvalidResource) {
            error = new Error(invalidResourceErrorMessage);
            error.name = localize(commonDemosMessages.azureResourceErrorTitle);
        } else if (isInvalidLanguage) {
            error = new Error(localize(commonDemosMessages.languageErrorMessage));
            error.name = localize(commonDemosMessages.languageErrorTitle);
        } else if (isInvalidInput) {
            error = new Error(localize(commonDemosMessages.inputErrorMessage));
            error.name = localize(commonDemosMessages.inputErrorTitle);
        }

        if (isInvalidInput || isInvalidLanguage || isInvalidResource) {
            const inputStep = document.querySelector('#demoPageDocumentInputStep');
            inputStep?.scrollIntoView({ behavior: 'smooth' });

            throw error;
        }
    };

    const postValidate = <T extends TextAnalyticsResultErrorsList>(response: T) => {
        const { errors } = response;
        if (errors?.length) {
            const e = new Error();
            e.name = errors[0].error.code;
            e.message = errors[0].error.message;

            throw e;
        }
    };

    const getConversationItems = (conversation: string, conversationId: string): ConversationItem[] => {
        const lines = conversation.split('\n');

        return lines
            .filter(line => line.trim())
            .map(line => {
                const indexOfFirstColon = line.indexOf(':');

                if (indexOfFirstColon === -1) {
                    throw new Error('Invalid format');
                }

                const speaker = line.slice(0, indexOfFirstColon).trim();
                const sentence = line.slice(indexOfFirstColon + 1).trim();

                return {
                    text: sentence,
                    participantId: speaker,
                    id: getId(`${conversationId}_`),
                    role: speaker as ConversationParticipantRole
                };
            });
    };

    const getConversationCurlCommand = ({
        tasks,
        language,
        conversation,
        languageAutoDetection
    }: {
        tasks: Object[];
        conversation: string;
        language?: Language['code'];
        languageAutoDetection?: boolean;
    }): string => {
        let conversationToPass: ConversationItem[] = null;
        try {
            conversationToPass = getConversationItems(conversation, 'conversationId');
        } catch {
            conversationToPass = null;
        }

        let payload: ConversationInput = { language, modality: 'text', id: 'conversationId', conversationItems: conversationToPass };

        const isLanguageAvailable = Boolean(
            languageAutoDetection ? detectedLanguages?.documents[0].detectedLanguage.iso6391Name : language
        );

        payload = {
            ...payload,
            ...(isLanguageAvailable && {
                language: languageAutoDetection ? detectedLanguages?.documents[0].detectedLanguage.iso6391Name : language
            })
        };

        return `@ECHO OFF

 curl -v -X POST "${azureResource?.endpoint ?? resourceEndpointCurlPlaceholder}language/analyze-conversations/jobs?api-version=${
            LuisConstants.Demos.ConversationApiVersion
        }" -H "Content-Type: application/json" -H "${authorizationHeader.key}: ${authorizationHeader.value}" --data-ascii "${JSON.stringify(
            {
                tasks,
                kind: 'Conversation',
                analysisInput: {
                    conversations: [
                        {
                            ...payload,
                            ...(!payload.conversationItems && {
                                conversationItems: localize(commonDemosMessages.conversationItemCurlPlaceholder)
                            })
                        }
                    ]
                }
            }
        )
            .split('"')
            .join('\\"')}" `;
    };

    const getConversationSummarization = ({
        language,
        sampleTitle,
        conversation,
        sentenceCount,
        summaryAspects,
        autoDetectLanguage
    }: {
        conversation: string;
        sampleTitle?: string;
        sentenceCount?: number;
        language?: Language['code'];
        autoDetectLanguage?: boolean;
        summaryAspects: SummaryAspects[];
    }) =>
        !isSignedIn
            ? flow(() =>
                  setConversationSummarization({
                      ...ConversationSummarizationDemoSamplesJsonOutput[sampleTitle],
                      summaries: ConversationSummarizationDemoSamplesJsonOutput[sampleTitle].summaries.filter(summary =>
                          summaryAspects.includes(summary.aspect)
                      )
                  })
              )()
            : flow(
                  resetResults,
                  async () => {
                      preValidate(autoDetectLanguage || Boolean(language), Boolean(conversation));

                      const conversationId = getId();
                      let conversationLanguage = language;

                      if (autoDetectLanguage) {
                          const languageResult = await textAnalyticsService.detectLanguage({
                              documents: [
                                  {
                                      id: getId(),
                                      text: conversation.trim().slice(0, conversation.length > 5000 ? 5000 : conversation.length)
                                  }
                              ]
                          });

                          setDetectedLanguages(languageResult);

                          conversationLanguage = languageResult.documents[0].detectedLanguage.iso6391Name;
                      }

                      await conversationService.getConversationSummarization({
                          sentenceCount,
                          summaryAspects,
                          conversations: [
                              {
                                  modality: 'text',
                                  id: conversationId,
                                  language: conversationLanguage,
                                  conversationItems: getConversationItems(conversation, conversationId)
                              }
                          ],
                          pollingCallHandler: res => {
                              postValidate(res);
                              if (res.status === 'succeeded') {
                                  const originalTask = res.tasks.items[0].results.conversations[0];
                                  const resolutionTask =
                                      summaryAspects.includes('resolution') &&
                                      summaryAspects.length > 1 &&
                                      res.tasks.items[1].results.conversations[0];
                                  setConversationSummarization({
                                      ...res.tasks.items[0].results.conversations[0],
                                      summaries: [...originalTask.summaries, ...(resolutionTask?.summaries ?? [])]
                                  });
                              }
                          }
                      });
                  },
                  addToast({ toastData: {} }),
                  addTracker({ tracker })
              )();

    return {
        detectedLanguages,
        conversationSummarization,
        conversationTracker: tracker,
        resetResults,
        getConversationItems,
        getConversationCurlCommand,
        getConversationSummarization
    };
};
